import type {
  RecipeFromImagesRequest,
  RecipeFromTextRequest,
  RecipeFromVideoRequest,
} from '@recipes/contracts';

/**
 * The user's input is kept in sessionStorage keyed by generation id so a provider
 * failure never loses it (spec §22): the review screen can retry or prefill the form.
 */
export type StoredGenerationRequest =
  | { kind: 'text'; body: RecipeFromTextRequest }
  | { kind: 'images'; body: RecipeFromImagesRequest; previews?: string[] }
  | { kind: 'video'; body: RecipeFromVideoRequest };

const PREFIX = 'rm.ai.request.';
const DRAFT_KEY = 'rm.ai.textDraft';

export function storeGenerationRequest(generationId: string, request: StoredGenerationRequest) {
  try {
    sessionStorage.setItem(PREFIX + generationId, JSON.stringify(request));
  } catch {
    // storage unavailable – the generation still works, only retry loses the input
  }
}

export function loadGenerationRequest(generationId: string): StoredGenerationRequest | null {
  try {
    const raw = sessionStorage.getItem(PREFIX + generationId);
    return raw ? (JSON.parse(raw) as StoredGenerationRequest) : null;
  } catch {
    return null;
  }
}

/** Unsent form state of /add/text so navigating away and back keeps the notes. */
export function storeTextDraft(body: Partial<RecipeFromTextRequest>) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(body));
  } catch {
    // ignore
  }
}

export function loadTextDraft(): Partial<RecipeFromTextRequest> | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Partial<RecipeFromTextRequest>) : null;
  } catch {
    return null;
  }
}

export function clearTextDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}
